/** @jsx jsx */
import type { ComponentType } from 'react';
import { jsx, css } from '@compiled/react';
import Placeholder from '@atlaskit/react-ufo/placeholder';
import { token } from '@atlaskit/tokens';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import type { CommentRepliesComponentProps as RepliesProps } from './replies-list/index.tsx';
import ReplyEditorContainer from './replies-editor-wrapper/index.tsx';

type CommentRepliesContainerProps = {
	fullIssueUrl: string | null;
	commentId: string;
	commentSessionId?: string | null;
	hasReplies: boolean;
};

const LazyRepliesList = lazyForPaint<ComponentType<RepliesProps>>(
	() => import(/* webpackChunkName: "async-reply-comments-list" */ './replies-list'),
);

const RepliesContainer = ({ hasReplies, ...otherProps }: CommentRepliesContainerProps) => {
	return (
		<div css={sectionContainerStyle}>
			{hasReplies && (
				<Placeholder name="reply-comments-list">
					<LazyRepliesList {...otherProps} />
				</Placeholder>
			)}
			<ReplyEditorContainer {...otherProps} />
		</div>
	);
};

const sectionContainerStyle = css({
	marginTop: token('space.negative.250'),
	'--commentSpacing': token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
	'& section:first-of-type:not(:last-of-type)': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not(:has([data-testid="issue-view-activity-comment.comment-reply-editor.reply-editor"]))': {
			position: 'relative',
			'&::before': {
				content: '',
				position: 'absolute',
				height: 'calc(100% - var(--commentSpacing))',
				top: 'var(--commentSpacing)',
				width: 'var(--commentLineWidth)',
				insetInlineStart: 'var(--commentInlineStart)',
				backgroundColor: 'var(--commentLineColor)',
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& section:not(:first-of-type):not(:last-of-type)': {
		position: 'relative',
		'&::before': {
			content: '',
			position: 'absolute',
			bottom: token('space.0'),
			width: 'var(--commentLineWidth)',
			insetInlineStart: 'var(--commentInlineStart)',
			backgroundColor: 'var(--commentLineColor)',
			height: 'calc(100% - var(--commentSpacing))',
			top: 'var(--commentSpacing)',
		},
	},
	// select all sections
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& section': {
		// which are not "show more replies" buttons
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
		'&:not(:has([data-testid="issue-view-activity-comment.comment-show-more-replies.show-more-button"]))':
			{
				// and do not have "show more replies" button as their immediate sibling
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
				'&:not(:has(+section [data-testid="issue-view-activity-comment.comment-show-more-replies.show-more-button"]))':
					{
						// and are not last-child themselves
						// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
						'&:not(:last-of-type)': {
							marginBottom: token('space.250'),
						},
					},
			},
	},
});

export default RepliesContainer;
